// src/interfaces/auth/AuthClient.ts
import { z } from "zod";
var AuthEvent = /* @__PURE__ */ ((AuthEvent2) => {
  AuthEvent2["SignOut"] = "SIGN_OUT";
  return AuthEvent2;
})(AuthEvent || {});
var CredentialsSchema = z.object({
  accessToken: z.string(),
  idToken: z.string(),
  refreshToken: z.string()
});

// src/interfaces/auth/PasswordlessClient.ts
var DeliveryMethod = /* @__PURE__ */ ((DeliveryMethod2) => {
  DeliveryMethod2["Email"] = "email";
  DeliveryMethod2["SMS"] = "sms";
  return DeliveryMethod2;
})(DeliveryMethod || {});

// src/PasswordlessAuthZeroClient.ts
import axios from "axios";

// src/AuthSessionClient.ts
import jwtDecode from "jwt-decode";
var AuthSessionClient = class _AuthSessionClient {
  constructor(credentials) {
    this.credentials = credentials;
    const { idToken, accessToken } = credentials;
    this.user = jwtDecode(idToken);
    this.accessTokenExpiry = _AuthSessionClient.getTokenExpiry(accessToken);
    this.idTokenExpiry = _AuthSessionClient.getTokenExpiry(idToken);
  }
  static createSession(credentials) {
    return new _AuthSessionClient(credentials);
  }
  static getTokenExpiry(token) {
    const data = jwtDecode(token);
    const epoch = data.exp;
    const expiry = epoch * 1e3;
    return expiry;
  }
  static isExpired(expiry, reference) {
    return expiry < reference;
  }
  isValid() {
    const now = Date.now();
    const accessIsValid = !_AuthSessionClient.isExpired(this.accessTokenExpiry, now);
    const idIsValid = !_AuthSessionClient.isExpired(this.idTokenExpiry, now);
    return idIsValid && accessIsValid;
  }
  getUser() {
    return this.user;
  }
  getAccessToken() {
    return this.credentials.accessToken;
  }
  getIdToken() {
    return this.credentials.idToken;
  }
  getRefreshToken() {
    return this.credentials.refreshToken;
  }
};

// src/BaseAuthClient.ts
import { addDays } from "date-fns";
var AuthTokenName = /* @__PURE__ */ ((AuthTokenName2) => {
  AuthTokenName2["AccessToken"] = "accessToken";
  AuthTokenName2["IdentityToken"] = "idToken";
  AuthTokenName2["RefreshToken"] = "refreshToken";
  return AuthTokenName2;
})(AuthTokenName || {});
var BaseAuthClient = class {
  constructor(storage, prefix, sessionClient, hub, autoRefresh = true) {
    this.storage = storage;
    this.prefix = prefix;
    this.sessionClient = sessionClient;
    this.hub = hub;
    this.autoRefresh = autoRefresh;
  }
  getStorageKey(name) {
    return `${this.prefix}.${name}`;
  }
  async getTokenExpiryDate(type, token) {
    if (type === "refreshToken" /* RefreshToken */) {
      return addDays(/* @__PURE__ */ new Date(), 30);
    }
    const expiry = AuthSessionClient.getTokenExpiry(token);
    return new Date(expiry);
  }
  async storeCredentials(credentials) {
    const keys = Object.keys(credentials);
    await Promise.all(
      keys.map(async (key) => {
        const storageKey = this.getStorageKey(key);
        const value = credentials[key];
        const date = await this.getTokenExpiryDate(key, value);
        const expireDate = new Date(date);
        return this.storage.setItem(storageKey, value, expireDate);
      })
    );
  }
  async clearCredentials() {
    const keys = Object.values(AuthTokenName);
    await Promise.all(keys.map((k) => this.storage.removeItem(this.getStorageKey(k))));
  }
  async initializeSession(credentials) {
    const session = this.sessionClient.createSession(credentials);
    if (!session.isValid()) {
      return;
    }
    this.session = session;
    await this.storeCredentials(credentials);
  }
  async clearSession() {
    await this.clearCredentials();
    this.session = void 0;
  }
  addEventListener(event, handler) {
    const eventHandler = (data) => {
      const isEvent = data.payload.event === event;
      if (isEvent) {
        handler();
      }
    };
    this.hub?.listen(this.prefix, eventHandler);
    return {
      remove: () => {
        this.hub?.remove(this.prefix, eventHandler);
      }
    };
  }
  dispatchEvent(event) {
    this.hub?.dispatch(this.prefix, { event });
  }
  async revalidateSession() {
    const newCredentials = await this.refreshTokens().catch(() => void 0);
    if (!newCredentials) {
      await this.clearCredentials();
      this.session = void 0;
      this.dispatchEvent("SIGN_OUT" /* SignOut */);
      return;
    }
    const newSession = this.sessionClient.createSession(newCredentials);
    if (newSession.isValid()) {
      this.session = newSession;
    }
  }
  async getSession() {
    if (this.session?.isValid()) {
      return this.session;
    }
    const currentCredentials = await this.getCredentials();
    if (!currentCredentials) {
      this.autoRefresh && await this.revalidateSession();
      return this.session;
    }
    const session = this.sessionClient.createSession(currentCredentials);
    if (session.isValid()) {
      this.session = session;
      return session;
    }
    if (this.autoRefresh) {
      await this.revalidateSession();
    }
    return this.session;
  }
  async getCredentials() {
    const keys = Object.values(AuthTokenName);
    const values = await Promise.all(
      keys.map(async (key) => {
        const storageKey = this.getStorageKey(key);
        const value = await this.storage.getItem(storageKey);
        if (!value) {
          return {};
        }
        return { [key]: value };
      })
    );
    const tokens = values.reduce((acc, val) => ({ ...acc, ...val }), {});
    const parsed = CredentialsSchema.safeParse(tokens);
    if (!parsed.success) {
      return void 0;
    }
    return parsed.data;
  }
  async signOut() {
    await this.clearSession();
    this.dispatchEvent("SIGN_OUT" /* SignOut */);
  }
};

// src/error/HttpError.ts
var HttpError = class extends Error {
  constructor(message, statusCode = 500) {
    super(message);
    this.statusCode = statusCode;
  }
};

// src/error/AuthZeroError.ts
var AuthZeroError = class extends HttpError {
  constructor(error) {
    const axiosError = error;
    const errorMessage = axiosError.response?.data?.error_description;
    const message = errorMessage || axiosError.message;
    const code = axiosError.response?.status;
    super(message, code);
  }
};

// src/InMemoryStorageClient.ts
var InMemoryStorage = class {
  constructor() {
    this.store = {};
  }
  getItem(key) {
    return this.store[key];
  }
  setItem(key, value) {
    this.store[key] = value;
  }
  removeItem(key) {
    delete this.store[key];
  }
  flush() {
    this.store = {};
  }
};
var inMemoryStorageClient = new InMemoryStorage();

// src/PasswordlessAuthZeroClient.ts
var ConnectionSendType = /* @__PURE__ */ ((ConnectionSendType2) => {
  ConnectionSendType2["CODE"] = "code";
  return ConnectionSendType2;
})(ConnectionSendType || {});
var GrantType = /* @__PURE__ */ ((GrantType2) => {
  GrantType2["OneTimePin"] = "http://auth0.com/oauth/grant-type/passwordless/otp";
  GrantType2["RefreshToken"] = "refresh_token";
  return GrantType2;
})(GrantType || {});
var deliveryKeys = {
  email: "email",
  sms: "phone_number"
};
var PasswordlessAuthZeroClient = class _PasswordlessAuthZeroClient extends BaseAuthClient {
  constructor(options, storage, sessionClient, hub, autoRefresh = true) {
    super(storage, "PasswordlessAuthZeroClient", sessionClient, hub, autoRefresh);
    this.options = options;
    this.scope = "openid profile email offline_access";
    const { domain } = options;
    this.httpClient = axios.create({
      baseURL: domain,
      headers: { "content-type": "application/json" }
    });
    this.baseURL = `https://${domain}`;
    this.audience = `${domain}/api/v2/`;
  }
  /**
   * Retrieves or creates an instance of PasswordlessAuthClient if it wasn't previously
   * instantiated
   *
   * @param options - Options used to make HTTP Request to the Auth0 API
   * @param storageClient - Client used to store and retrieve data
   * @param sessionClient - CLient used to manage the session of the user
   *
   * @returns An instance of PasswordlessAuthClient
   */
  static getInstance(options, storageClient, sessionClient, hub) {
    if (!_PasswordlessAuthZeroClient.instance) {
      const storage = storageClient ? storageClient : inMemoryStorageClient;
      const resolvedSessionClient = sessionClient ? sessionClient : AuthSessionClient;
      _PasswordlessAuthZeroClient.instance = new _PasswordlessAuthZeroClient(
        options,
        storage,
        resolvedSessionClient,
        hub
      );
    }
    return _PasswordlessAuthZeroClient.instance;
  }
  get clientId() {
    return this.options.clientId;
  }
  async startAuthentication(mode, username) {
    try {
      const key = deliveryKeys[mode];
      const input = {
        [key]: username,
        connection: mode
      };
      await this.httpClient.post("/passwordless/start", {
        client_id: this.clientId,
        send: "code" /* CODE */,
        ...input
      });
      const field = username.includes("@") ? "email" : "phone";
      return {
        username,
        field
      };
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }
  async completeAuthentication(mode, username, code) {
    try {
      const { data } = await this.httpClient.post("/oauth/token", {
        client_id: this.clientId,
        grant_type: "http://auth0.com/oauth/grant-type/passwordless/otp" /* OneTimePin */,
        otp: code,
        realm: mode,
        username,
        audience: this.audience,
        scope: this.scope
      });
      const { access_token: accessToken, refresh_token: refreshToken, id_token: idToken } = data;
      const credentials = { accessToken, idToken, refreshToken };
      await this.initializeSession(credentials);
      return credentials;
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }
  getUser() {
    throw new Error("Method not implemented.");
  }
  async refreshTokens() {
    const storageKey = this.getStorageKey("refreshToken" /* RefreshToken */);
    const refreshToken = await this.storage.getItem(storageKey);
    if (!refreshToken) {
      return void 0;
    }
    try {
      const path = `${this.baseURL}/oauth/token`;
      const response = await fetch(path, {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          client_id: this.clientId,
          grant_type: "refresh_token" /* RefreshToken */,
          refresh_token: refreshToken,
          audience: this.audience,
          scope: this.scope
        })
      });
      if (!response.ok) {
        return void 0;
      }
      const data = await response.json();
      const { access_token: accessToken, id_token: idToken } = data;
      const credentials = { accessToken, idToken, refreshToken };
      if (this.autoRefresh) {
        await this.initializeSession(credentials);
      }
      return credentials;
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }
};
export {
  AuthEvent,
  AuthSessionClient,
  AuthTokenName,
  BaseAuthClient,
  ConnectionSendType,
  DeliveryMethod,
  GrantType,
  InMemoryStorage,
  PasswordlessAuthZeroClient,
  inMemoryStorageClient
};
//# sourceMappingURL=index.mjs.map